// ts-rest contract for the Desk Internal Suitability Assessment (WP-G).
//
// The IM fills the Septima "Internal Suitability Assessment" form AFTER the
// client completes the Fact Find (internal-use only). The endpoints: a `get`
// (returns the client's current assessment, or a PREFILLED blank draft seeded
// from the Client + Fact Find when none exists yet), `save` (upsert draft),
// and `complete` (draft → completed).
//
// Per ADR-0018 the DTO is a real `z.object` of the stored raw inputs plus a
// `derived` block the controller computes via `computeSuitabilityAssessment`
// (the web form recomputes the same derivations live as the IM types, so the
// `derived` block is a convenience mirror, not the source of truth).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

const errorBody = z.object({ error: z.string() });

// --- enums (snake_case domain values, camelCase keys) ---
const statusSchema = z.enum(['draft', 'completed']);
const clientTypeSchema = z.enum(['individual', 'entity']);
const classificationSchema = z.enum(['retail', 'professional', 'hnw']);
// The entity form's classification axis (Retail vs Elective Professional Client).
const entityClassificationSchema = z.enum(['retail', 'epc']);
const employmentStatusSchema = z.enum([
    'employed',
    'self_employed',
    'retired',
    'director',
    'other',
]);
const serviceRecommendationSchema = z.enum(['dsc', 'adv', 'adv_managed']);
const riskProfileSchema = z.enum([
    'low',
    'moderately_low',
    'moderate',
    'moderately_high',
    'high',
    'aim_iht',
]);
const complianceResultSchema = z.enum(['approved', 'rejected', 'more_information_required']);

// --- the raw, IM-entered fields (what gets stored + what `save` collects) ---
// Every field is nullable: a draft is partial by nature.
export const suitabilityAssessmentFieldsSchema = z.object({
    clientTitle: z.string().nullable(),
    clientForenames: z.string().nullable(),
    clientSurname: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    accountNumber: z.string().nullable(),
    // Entity variant only (null for individuals).
    entityName: z.string().nullable(),
    entityClassification: entityClassificationSchema.nullable(),
    representativeClassification: entityClassificationSchema.nullable(),
    investmentManagerName: z.string().nullable(),
    reviewDate: z.string().nullable(),
    lastReviewDate: z.string().nullable(),

    isBusinessOwner: z.boolean().nullable(),
    isFinancialServicesProfessional: z.boolean().nullable(),
    clientClassification: classificationSchema.nullable(),
    otherRelevantExperience: z.string().nullable(),

    currentVulnerabilityScore: z.number().nullable(),
    newVulnerabilityScore: z.number().nullable(),
    vulnerabilityChangeComments: z.string().nullable(),

    septimaPortfolioExclSippIsa: z.number().nullable(),
    septimaPortfolioExclSippNonIsa: z.number().nullable(),
    cashSavingsIsa: z.number().nullable(),
    cashSavingsNonIsa: z.number().nullable(),
    mainPropertyValue: z.number().nullable(),
    otherPropertyValue: z.number().nullable(),
    personalPensionSeptimaSipp: z.number().nullable(),
    personalPensionOther: z.number().nullable(),
    otherPensionAssets: z.number().nullable(),
    businessInterests: z.number().nullable(),
    otherMaterialAssetsIsa: z.number().nullable(),
    otherMaterialAssetsNonIsa: z.number().nullable(),
    mortgageOutstanding: z.number().nullable(),
    anticipatedMortgagePayOffDate: z.string().nullable(),
    otherMaterialDebts: z.number().nullable(),

    employmentStatus: employmentStatusSchema.nullable(),
    employmentStatusOther: z.string().nullable(),
    employmentIncome: z.number().nullable(),
    septimaTaxableIncome: z.number().nullable(),
    septimaIsaIncome: z.number().nullable(),
    statePensionIncome: z.number().nullable(),
    otherPensionIncome: z.number().nullable(),
    otherInvestmentIncome: z.number().nullable(),
    allOtherIncome: z.number().nullable(),
    annualMortgageRentExpenditure: z.number().nullable(),
    annualEssentialExpenditure: z.number().nullable(),
    annualDiscretionaryExpenditure: z.number().nullable(),
    annualOtherExpenditure: z.number().nullable(),

    totalInvestableAssetsOverride: z.number().nullable(),
    capitalRequirements: z.number().nullable(),
    totalPortfoliosAtSeptima: z.number().nullable(),
    illiquidAssets: z.number().nullable(),
    totalClientWealthOverride: z.number().nullable(),
    incomeYieldAssumption: z.number().nullable(),

    liquidityComment: z.string().nullable(),
    serviceRecommendation: serviceRecommendationSchema.nullable(),
    serviceRationale: z.string().nullable(),
    riskProfileRecommendation: riskProfileSchema.nullable(),
    riskProfileRationale: z.string().nullable(),
    strategyRecommendation: z.string().nullable(),
    strategyRationale: z.string().nullable(),

    declInformationCollected: z.boolean().nullable(),
    declCircumstancesAssessed: z.boolean().nullable(),
    declStrategyRecommended: z.boolean().nullable(),
    declRiskWarningsProvided: z.boolean().nullable(),
    declInvestmentRestrictionsConsidered: z.boolean().nullable(),
    declCostChargesDisclosed: z.boolean().nullable(),
    signatureName: z.string().nullable(),
    signatureDate: z.string().nullable(),
    peerReviewName: z.string().nullable(),
    peerReviewDate: z.string().nullable(),
    peerReviewComments: z.string().nullable(),
    complianceApprovalName: z.string().nullable(),
    complianceApprovalDate: z.string().nullable(),
    complianceApprovalResult: complianceResultSchema.nullable(),
    complianceApprovalComments: z.string().nullable(),
});
export type SuitabilityAssessmentFields = z.infer<typeof suitabilityAssessmentFieldsSchema>;

// --- the controller-derived figures (mirror of computeSuitabilityAssessment) ---
export const suitabilityAssessmentDerivedSchema = z.object({
    totalAssets: z.number(),
    totalLiabilities: z.number(),
    netAssets: z.number(),
    totalIncome: z.number(),
    totalOutgoings: z.number(),
    totalInvestableAssets: z.number(),
    capacityForLossPct: z.number(),
    capacityForLossBand: z.enum(['very_low', 'low', 'medium', 'high', 'very_high']),
    capacityForLossBandLabel: z.string(),
    portfolioFallTolerancePct: z.number(),
    totalClientWealth: z.number(),
    totalLiquidityPct: z.number(),
    liquidityRating: z.enum([
        'liquidity_risk',
        'potentially_constrained',
        'adequately_liquid',
        'highly_liquid',
    ]),
    liquidityRatingLabel: z.string(),
    nonSeptimaIncomeCoversStressedOutgoings: z.boolean(),
    cashCoversSixMonths: z.boolean(),
    cashCoversTwelveMonths: z.boolean(),
});
// Wire-shape mirror of the helper's `SuitabilityAssessmentDerived` (kept local
// to avoid an export-name clash with the helper; structurally identical).
export type SuitabilityAssessmentDerivedDto = z.infer<typeof suitabilityAssessmentDerivedSchema>;

// --- the full assessment DTO ---
export const suitabilityAssessmentDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    imUserId: z.string(),
    status: statusSchema,
    clientType: clientTypeSchema,
    fields: suitabilityAssessmentFieldsSchema,
    derived: suitabilityAssessmentDerivedSchema,
    createdAt: z.string(),
    completedAt: z.string().nullable(),
});
export type SuitabilityAssessmentDto = z.infer<typeof suitabilityAssessmentDtoSchema>;

// The GET response: the current assessment (`exists: true`) OR a prefilled
// blank draft (`exists: false`) seeded from the Client + Fact Find. `clientName`
// is always present for the page header.
export const suitabilityAssessmentGetResponseSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    exists: z.boolean(),
    // The current saved assessment when `exists`, else null.
    assessment: suitabilityAssessmentDtoSchema.nullable(),
    // A prefilled `fields` block to seed a fresh form when no assessment exists
    // (and a convenience for diffing prefilled vs entered). Sourced from real
    // Client + Fact Find data only (ADR-0005) — un-prefillable fields are null.
    prefill: suitabilityAssessmentFieldsSchema,
    // Suggested form variant for a fresh assessment, derived from the Client's
    // legal-entity type. (When `exists`, read `assessment.clientType` instead.)
    suggestedClientType: clientTypeSchema,
});
export type SuitabilityAssessmentGetResponse = z.infer<
    typeof suitabilityAssessmentGetResponseSchema
>;

export const suitabilityAssessmentContract = c.router(
    {
        getSuitabilityAssessment: {
            method: 'GET',
            path: '/desk/suitability/assessment/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            responses: {
                200: suitabilityAssessmentGetResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A client's Internal Suitability Assessment (or a prefilled blank draft)",
        },
        saveSuitabilityAssessment: {
            method: 'POST',
            path: '/desk/suitability/assessment/save',
            body: z.object({
                // Absent on first save; present to patch an existing draft.
                id: z.string().optional(),
                clientId: z.string().min(1),
                // Set on create (defaults to `individual`); a patch may omit it.
                clientType: clientTypeSchema.optional(),
                fields: suitabilityAssessmentFieldsSchema.partial(),
            }),
            responses: {
                200: z.object({ assessment: suitabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Create or patch the working draft of a suitability assessment',
        },
        completeSuitabilityAssessment: {
            method: 'POST',
            path: '/desk/suitability/assessment/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ assessment: suitabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Sign off a suitability assessment (draft → completed)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
